import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../features/user-dashboard/auth/AuthContext';
import adminService from '../services/adminService';
import type { User } from '../types/auth';

function isAdminUser(u: User | null): boolean {
  if (!u) return false;
  return u.is_superuser === true || u.role === 'admin';
}

export default function AdminVegetationPage() {
  const navigate = useNavigate();
  const { user: me, loading: authLoading } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<{ status: string; message: string; result?: any } | null>(null);
  const [error, setError] = useState('');

  if (authLoading || (me && !isAdminUser(me))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  if (me && !isAdminUser(me)) {
    navigate('/', { replace: true });
    return null;
  }

  const handleTrigger = async () => {
    setProcessing(true);
    setResult(null);
    setError('');
    try {
      const data = await adminService.triggerVegetationProcessing();
      setResult(data);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to trigger vegetation processing';
      setError(String(msg));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <AdminLayout title="Vegetation Processing">
      <div className="mb-4 p-3 rounded-lg bg-amber-50 text-amber-950 border border-amber-200 text-sm">
        Satellite Vegetation/Moisture snapshot alerts are currently paused
        (<code className="text-xs">VEGETATION_SNAPSHOT_ALERTS_ENABLED=False</code>).
        Triggering will no-op until that flag is re-enabled.
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Manually trigger the NDVI/NDMI vegetation processing pipeline for all farms. This will:
      </p>
      <ol className="text-sm text-gray-600 mb-6 list-decimal list-inside space-y-1">
        <li>Fetch all farm geometries from the external explorer API</li>
        <li>Process Sentinel-2 satellite data via Google Earth Engine</li>
        <li>Calculate NDVI and NDMI vegetation indices</li>
        <li>Generate alerts and store results in the database</li>
      </ol>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-900 border border-red-200 text-sm">
          {error}
        </div>
      )}

      {result && (
        <div className="mb-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-sm">
          <div className="font-semibold text-emerald-900 mb-1">
            {result.status === 'success' ? 'Processing completed successfully' : result.status}
          </div>
          <div className="text-emerald-800 mb-1">{result.message}</div>
          {result.result && (
            <pre className="mt-2 p-2 bg-emerald-100 rounded text-xs overflow-x-auto">
              {JSON.stringify(result.result, null, 2)}
            </pre>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={handleTrigger}
        disabled={processing}
        className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {processing ? (
          <>
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            Processing...
          </>
        ) : (
          <>
            <i className="ri-rocket-line" />
            Trigger Vegetation Processing
          </>
        )}
      </button>
    </AdminLayout>
  );
}
